/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  Node,
  Edge,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import { useSelector, useDispatch } from 'react-redux';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { store } from 'src/redux/store';
import {
  clearAllNodeConfigurations,
  clearSelectedTrigger,
  setSelectedTrigger,
  setNodeConfiguration,
  removeNodeConfiguration,
} from 'src/redux/workflows/slice';
import 'reactflow/dist/style.css';
import { Container } from 'src/components/layouts/Container/Container';
import NodePanel from 'src/components/nodes/NodePanel/NodePanel';
import { workflowEdgeTypes, workflowNodeTypes } from 'src/components/nodes/nodeTypes';
import useWorkflows from 'src/hooks/useWorkflows';
import SaveWorkflowModal from 'src/components/modals/SaveWorkflowModal/SaveWorkflowModal';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import { useGetWorkflow, useUpdateWorkflow, WorkflowRunStats } from 'src/services/workflows';
import { SegmentedControl } from 'src/components/ui/SegmentedControl/SegmentedControl';
import { WorkflowValidationContext } from 'src/components/nodes/WorkflowValidationContext';
import { validateWorkflow } from 'src/utils/workflowValidation';
import { RECORD_CONTACT_RECIPIENT } from 'src/utils/workflowRecipients';
import './styles/Workflows.scss';
import { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { formatConditionValue, formatActionTypeKey, mapFieldTypeToValueType } from 'src/utils/general';
import WorkflowRuns from './Workflows/WorkflowRuns/WorkflowRuns';

const EMPTY_RUN_STATS: WorkflowRunStats = {
  total: 0,
  success: 0,
  failed: 0,
  skipped: 0,
  lifetime: { total: 0, success: 0, failed: 0, skipped: 0 },
};

function WorkflowView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const workflowsState = useSelector(getWorkflows);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(searchParams.get('edit') === 'true');
  const [workflowNodes, setWorkflowNodes] = useState<Node[]>([]);
  const [workflowEdges, setWorkflowEdges] = useState<Edge[]>([]);
  const [flowResetKey, setFlowResetKey] = useState(0);
  const [activeTab, setActiveTab] = useState<'structure' | 'runs'>('structure');
  const [showIssues, setShowIssues] = useState(false);
  const [oneTime, setOneTime] = useState(true);
  const nodesRef = useRef(workflowNodes);
  nodesRef.current = workflowNodes;
  const edgesRef = useRef(workflowEdges);
  edgesRef.current = workflowEdges;
  const idCounterRef = useRef(100);
  const [initialWorkflowState, setInitialWorkflowState] = useState<{
    selectedTrigger: any;
    nodeConfigurations: any;
  } | null>(null);

  const { id } = useParams();

  const { data: workflow, isLoading: isWorkflowLoading } = useGetWorkflow(Number(id));

  const [title, setTitle] = useState(workflow?.name || '');
  const [description, setDescription] = useState(workflow?.description || '');

  useEffect(() => {
    setTitle(workflow?.name || '');
    setDescription(workflow?.description || '');
    setOneTime(workflow?.one_time ?? true);
  }, [workflow]);

  const onNodesChange = useCallback((changes: any) => {
    setWorkflowNodes(nodes => applyNodeChanges(changes, nodes));
  }, []);

  const onEdgesChange = useCallback((changes: any) => {
    setWorkflowEdges(edges => applyEdgeChanges(changes, edges));
  }, []);

  const removeNode = useCallback(
    (nodeId: string) => {
      if (!isEditMode) return;

      // Use functional updater for edges to avoid stale closure
      setWorkflowEdges(currentEdges => {
        const edgeIntoNode = currentEdges.find(e => e.target === nodeId);
        const edgeFromNode = currentEdges.find(e => e.source === nodeId);

        const trailingPlusId = edgeFromNode?.target;
        const edgeFromPlus = trailingPlusId ? currentEdges.find(e => e.source === trailingPlusId) : null;
        const precedingPlusId = edgeIntoNode?.source;

        const nodeIdsToRemove = new Set<string>([nodeId]);
        if (trailingPlusId) nodeIdsToRemove.add(trailingPlusId);

        setWorkflowNodes(currentNodes => {
          const nodeToRemove = currentNodes.find(node => node.id === nodeId);
          if (!nodeToRemove || nodeToRemove.type === 'trigger') return currentNodes;
          return currentNodes.filter(node => !nodeIdsToRemove.has(node.id));
        });

        const filtered = currentEdges.filter(e => !nodeIdsToRemove.has(e.source) && !nodeIdsToRemove.has(e.target));

        if (precedingPlusId && edgeFromPlus) {
          const nextNodeId = edgeFromPlus.target;
          return [
            ...filtered,
            {
              id: `e${precedingPlusId}-${nextNodeId}`,
              source: precedingPlusId,
              target: nextNodeId,
              type: 'standard',
            },
          ];
        }

        return filtered;
      });

      const nodeConfig = workflowsState.nodeConfigurations[nodeId];
      if (nodeConfig) {
        dispatch(removeNodeConfiguration(nodeId));
      }
    },
    [isEditMode, workflowsState.nodeConfigurations, dispatch],
  );

  const onAddNode = useCallback(
    (parentId: string, type?: string, actionData?: any) => {
      if (!isEditMode) return;

      const getNodeWidth = (nodeType: string) => {
        switch (nodeType) {
          case 'emailBuilder':
            return 640;
          case 'notificationBuilder':
            return 480;
          case 'caseBuilder':
            return 480;
          case 'productBuilder':
            return 480;
          default:
            return 320;
        }
      };

      const NODE_GAP = 100;
      const PLUS_NODE_WIDTH = 32;

      // Use refs for current state to avoid stale closures
      const currentEdges = edgesRef.current;
      const outgoingEdge = currentEdges.find(e => e.source === parentId);
      const isMiddleInsertion = !!outgoingEdge;

      // Generate unique IDs
      const nodeId = idCounterRef.current++;
      const plusNodeId = idCounterRef.current++;
      const newNodeId = `${nodeId}-${type}`;
      const newPlusNodeId = `${plusNodeId}-plus`;

      setWorkflowNodes(currentNodes => {
        const parentNode = currentNodes.find(node => node.id === parentId);
        if (!parentNode) return currentNodes;

        const newNodeWidth = getNodeWidth(type || 'condition');
        const newNodeX = parentNode.position.x;
        const newNodeY = parentNode.position.y - 20.8;

        const newNode: Node = {
          id: newNodeId,
          type: type || 'condition',
          position: { x: newNodeX, y: newNodeY },
          data: {
            label: type || 'condition',
            onAddNode,
            removeLastNode: () => {},
            scrollToPlusNode: () => {},
            removeNode,
            isEditMode,
            ...(actionData && { actionData }),
          },
        };

        const newPlusNode: Node = {
          id: newPlusNodeId,
          type: 'plus',
          position: {
            x: newNodeX + newNodeWidth + NODE_GAP,
            y: parentNode.position.y,
          },
          data: {
            label: 'Plus',
            onAddNode,
            removeLastNode: () => {},
            scrollToPlusNode: () => {},
            removeNode,
            isEditMode,
          },
        };

        // Remove the clicked plus node
        let updatedNodes = currentNodes.filter(node => node.id !== parentId);

        if (isMiddleInsertion && outgoingEdge) {
          const nextNodeId = outgoingEdge.target;
          const nextNode = currentNodes.find(n => n.id === nextNodeId);
          if (nextNode) {
            const shiftAmount = newNodeWidth + NODE_GAP + PLUS_NODE_WIDTH + NODE_GAP;
            const shiftThreshold = nextNode.position.x;
            updatedNodes = updatedNodes.map(n => {
              if (n.position.x >= shiftThreshold) {
                return { ...n, position: { ...n.position, x: n.position.x + shiftAmount } };
              }
              return n;
            });
          }
        }

        return [...updatedNodes, newNode, newPlusNode];
      });

      setWorkflowEdges(currentEdges => {
        const edgeToPlus = currentEdges.find(edge => edge.target === parentId);
        const edgeFromPlus = currentEdges.find(edge => edge.source === parentId);

        const filteredEdges = currentEdges.filter(edge => edge.target !== parentId && edge.source !== parentId);

        const newEdges: Edge[] = [];

        if (edgeToPlus) {
          newEdges.push({
            id: `e${edgeToPlus.source}-${newNodeId}`,
            source: edgeToPlus.source,
            target: newNodeId,
            type: 'standard',
          });
        }

        newEdges.push({
          id: `e${newNodeId}-${newPlusNodeId}`,
          source: newNodeId,
          target: newPlusNodeId,
          type: 'standard',
        });

        if (edgeFromPlus) {
          newEdges.push({
            id: `e${newPlusNodeId}-${edgeFromPlus.target}`,
            source: newPlusNodeId,
            target: edgeFromPlus.target,
            type: 'standard',
          });
        }

        return [...filteredEdges, ...newEdges];
      });
    },
    [isEditMode, removeNode],
  );

  const panelOptions = useMemo(() => {
    const getLargestPlusElement = () => {
      let largestElement = null;
      let largestNumber = -Infinity;

      workflowNodes.forEach((node: any) => {
        const match = node.id.match(/^(\d+)-plus$/);
        if (match) {
          const number = parseInt(match[1], 10);
          if (number > largestNumber) {
            largestNumber = number;
            largestElement = node;
          }
        }
      });

      return largestElement;
    };

    const largestPlusElement = getLargestPlusElement();

    return { onAddNode, largestPlusElement, clickedPlusNodeId: workflowsState.clickedPlusNodeId };
  }, [workflowNodes, onAddNode, workflowsState.clickedPlusNodeId]);

  const getTargetPlusNode = useCallback(() => {
    if (workflowsState.clickedPlusNodeId) {
      return workflowNodes.find(n => n.id === workflowsState.clickedPlusNodeId) || null;
    }
    return workflowNodes.reduceRight((acc: any, node: any) => {
      if (!acc && node.type === 'plus') return node;
      return acc;
    }, null);
  }, [workflowNodes, workflowsState.clickedPlusNodeId]);

  const calculateLeftPosition = useMemo(() => {
    const targetPlusNode = getTargetPlusNode();
    if (!targetPlusNode) return 810;

    const xPosition = parseFloat(targetPlusNode.position.x) || 0;
    return xPosition + 100;
  }, [workflowNodes, workflowsState.showNodesType, workflowsState.clickedPlusNodeId]);

  const calculateTopPosition = useMemo(() => {
    const targetPlusNode = getTargetPlusNode();
    if (!targetPlusNode) return 115;

    const yPosition = parseFloat(targetPlusNode.position.y) || 0;
    return yPosition + 65;
  }, [workflowNodes, workflowsState.showNodesType, workflowsState.clickedPlusNodeId]);

  const updateWorkflowMutation = useUpdateWorkflow();

  const nodeTypes = workflowNodeTypes;
  const edgeTypes = workflowEdgeTypes;

  const transformWorkflowToNodesAndEdges = (workflowData: any) => {
    if (!workflowData?.root) return { nodes: [], edges: [] };

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let nodeCount = 1;

    const processWorkflowItem = (item: any, parentId?: string, position = { x: 100, y: 0 }): string => {
      const currentNodeId = `${nodeCount}-${item.item_type}`;
      nodeCount += 1;

      const nodeType = item.item_type;

      const newNode: Node = {
        id: currentNodeId,
        type: nodeType,
        position,
        data: {
          label: item.item_type,
          onAddNode,
          removeLastNode: () => {},
          scrollToPlusNode: () => {},
          removeNode,
          isEditMode,
          ...(item.item_type === 'trigger' &&
            item.related_object && {
              triggerData: {
                id: item.related_object.id,
                name: item.related_object.name || 'Trigger',
                model: item.related_object.model || '',
              },
            }),
          ...(item.item_type === 'condition' &&
            item.related_object && {
              conditionData: item.related_object.attribute
                ? {
                    attribute: item.related_object.attribute,
                    condition: item.related_object.condition,
                    value: item.related_object.value,
                    valueType: item.related_object.value_type || 'string',
                  }
                : {
                    operator: item.related_object.operator || 'and',
                    conditions: (item.related_object.condition_ids || []).map((c: any) => ({
                      attribute: c.attribute,
                      condition: c.condition,
                      value: c.value,
                      valueLabel: c.value_label || c.value,
                      valueType: c.value_type || 'string',
                    })),
                  },
            }),
          ...(item.item_type === 'action' &&
            item.related_object && {
              actionData: {
                actionType: item.related_object.action_type,
                title: item.related_object.title || '',
                returnValue: item.related_object.return_value || '',
                content: item.related_object.return_value || '',
                message: item.related_object.return_value || '',
                ...(item.related_object.action_type === 'product' && {
                  selectedProductId: item.related_object.return_value
                    ? parseInt(item.related_object.return_value, 10)
                    : undefined,
                  unit: item.related_object.detail_values?.unit,
                  amount: item.related_object.detail_values?.amount,
                }),
                ...(item.related_object.action_type === 'case' && {
                  sectionId: item.related_object.detail_values?.section,
                }),
                ...(item.related_object.action_type === 'notification' && {
                  partnerId: item.related_object.detail_values?.recipient_user_id,
                  partnerName: item.related_object.detail_values?.recipient_user_name,
                }),
                ...(item.related_object.action_type === 'mail' && {
                  recipient: item.related_object.detail_values?.recipient_email,
                }),
              },
            }),
        },
      };

      nodes.push(newNode);

      if (item.item_type === 'trigger' && item.related_object) {
        dispatch(
          setSelectedTrigger({
            id: item.related_object.id,
            name: item.related_object.name || 'Trigger',
            model: item.related_object.model || '',
          }),
        );
      } else if (item.item_type === 'condition' && item.related_object) {
        // Handle both legacy single-condition and new group format
        const conditionGroupData = item.related_object.attribute
          ? {
              operator: 'and' as const,
              conditions: [
                {
                  attribute: item.related_object.attribute,
                  condition: item.related_object.condition,
                  value: item.related_object.value,
                  valueType: item.related_object.value_type || 'string',
                },
              ],
              subgroups: [],
            }
          : {
              operator: item.related_object.operator || 'and',
              conditions: (item.related_object.condition_ids || []).map((c: any) => ({
                attribute: c.attribute,
                condition: c.condition,
                value: c.value,
                valueLabel: c.value_label || c.value,
                valueType: c.value_type || 'string',
              })),
              subgroups: [],
            };

        dispatch(
          setNodeConfiguration({
            nodeId: currentNodeId,
            nodeType: 'condition',
            data: conditionGroupData,
          }),
        );
      } else if (item.item_type === 'action' && item.related_object) {
        const actionData: any = {
          actionType: item.related_object.action_type,
          title: item.related_object.title || '',
          returnValue: item.related_object.return_value || '',
          content: item.related_object.return_value || '',
          message: item.related_object.return_value || '',
        };

        if (item.related_object.action_type === 'product') {
          if (item.related_object.return_value) {
            actionData.selectedProductId = parseInt(item.related_object.return_value, 10);
          }
          if (item.related_object.detail_values) {
            actionData.unit = item.related_object.detail_values.unit;
            actionData.amount = item.related_object.detail_values.amount;
          }
        }

        if (item.related_object.action_type === 'case' && item.related_object.detail_values) {
          if (item.related_object.detail_values.section) {
            actionData.sectionId = item.related_object.detail_values.section;
          }
        }

        if (item.related_object.action_type === 'notification' && item.related_object.detail_values) {
          actionData.partnerId = item.related_object.detail_values.recipient_user_id;
          actionData.partnerName = item.related_object.detail_values.recipient_user_name;
        }

        if (item.related_object.action_type === 'mail' && item.related_object.detail_values) {
          actionData.recipient = item.related_object.detail_values.recipient_email;
        }

        dispatch(
          setNodeConfiguration({
            nodeId: currentNodeId,
            nodeType: 'action',
            data: actionData,
          }),
        );
      }

      if (parentId) {
        const newEdge: Edge = {
          id: `e${parentId}-${currentNodeId}`,
          source: parentId,
          target: currentNodeId,
          type: 'standard',
        };
        edges.push(newEdge);
      }

      const getNodeWidth = (type: string) => {
        switch (type) {
          case 'emailBuilder':
            return 640;
          case 'notificationBuilder':
            return 480;
          case 'caseBuilder':
            return 480;
          case 'productBuilder':
            return 480;
          default:
            return 320;
        }
      };

      const NODE_GAP = 100;
      const PLUS_NODE_WIDTH = 32;
      const nodeWidth = getNodeWidth(nodeType);
      let nextParentId = currentNodeId;
      let nextX = position.x + nodeWidth + NODE_GAP;

      if (isEditMode) {
        const plusNodeId = `${nodeCount}-plus`;
        nodeCount += 1;

        const plusNode: Node = {
          id: plusNodeId,
          type: 'plus',
          position: { x: position.x + nodeWidth + NODE_GAP, y: position.y + 20.8 },
          data: {
            label: 'Plus',
            onAddNode,
            removeLastNode: () => {},
            scrollToPlusNode: () => {},
            removeNode,
            isEditMode,
          },
        };
        nodes.push(plusNode);
        edges.push({
          id: `e${currentNodeId}-${plusNodeId}`,
          source: currentNodeId,
          target: plusNodeId,
          type: 'standard',
        });
        nextParentId = plusNodeId;
        nextX = position.x + nodeWidth + NODE_GAP + PLUS_NODE_WIDTH + NODE_GAP;
      }

      if (item.true_result) {
        processWorkflowItem(item.true_result, nextParentId, { x: nextX, y: position.y });
      }

      if (item.false_result) {
        const falsePosition = {
          x: position.x + 100,
          y: position.y + 150,
        };
        processWorkflowItem(item.false_result, currentNodeId, falsePosition);
      }

      return currentNodeId;
    };

    processWorkflowItem(workflowData.root);
    return { nodes, edges };
  };

  useEffect(() => {
    const editParam = searchParams.get('edit') === 'true';
    if (editParam !== isEditMode) {
      setIsEditMode(editParam);
    }
  }, [searchParams, isEditMode]);

  useEffect(() => {
    dispatch(clearAllNodeConfigurations());
    dispatch(clearSelectedTrigger());
  }, [id, dispatch]);

  useEffect(() => {
    if (workflow) {
      dispatch(clearAllNodeConfigurations());
      dispatch(clearSelectedTrigger());

      const { nodes: transformedNodes, edges: transformedEdges } = transformWorkflowToNodesAndEdges(workflow);
      setWorkflowNodes(transformedNodes);
      setWorkflowEdges(transformedEdges);
    }
  }, [workflow, isEditMode]);

  useEffect(() => {
    setWorkflowNodes(prevNodes =>
      prevNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onAddNode,
          removeLastNode: () => {},
          scrollToPlusNode: () => {},
          removeNode,
          isEditMode,
          oneTime,
        },
      })),
    );
  }, [isEditMode, onAddNode, removeNode, oneTime]);

  useEffect(() => {
    return () => {
      dispatch(clearAllNodeConfigurations());
      dispatch(clearSelectedTrigger());
    };
  }, [dispatch]);

  const transformWorkflowDataForUpdate = (
    title: string,
    description: string,
    nodes: Node[],
    edges: Edge[],
    oneTimeValue: boolean,
  ) => {
    const triggerNode = nodes.find(node => node.type === 'trigger');

    if (!triggerNode) {
      throw new Error('No trigger node found in workflow');
    }

    const findNextWorkflowNode = (nodeId: string): string | null => {
      const outgoingEdges = edges.filter(edge => edge.source === nodeId);

      const validEdge = outgoingEdges.find(edge => {
        const targetNode = nodes.find(n => n.id === edge.target);
        return targetNode !== undefined;
      });

      if (!validEdge) {
        return null;
      }

      const targetNode = nodes.find(n => n.id === validEdge.target);
      if (!targetNode) {
        return null;
      }

      if (targetNode.type === 'plus') {
        return findNextWorkflowNode(validEdge.target);
      }
      return validEdge.target;
    };

    const buildWorkflowItem = (nodeId: string): any => {
      const node = nodes.find(n => n.id === nodeId);
      if (!node || node.type === 'plus') return null;

      const nodeConfig = workflowsState.nodeConfigurations[nodeId];
      let relatedObject: any;
      let itemType: string;

      switch (node.type) {
        case 'trigger':
          if (!workflowsState.selectedTrigger) {
            throw new Error('No trigger selected');
          }
          itemType = 'trigger';
          relatedObject = {
            id: workflowsState.selectedTrigger.id,
            model: workflowsState.selectedTrigger.model,
          };
          break;

        case 'condition': {
          if (!nodeConfig || nodeConfig.nodeType !== 'condition') {
            throw new Error(`No configuration found for condition node ${nodeId}`);
          }
          itemType = 'condition';
          const conditionData = nodeConfig.data as any;
          relatedObject = {
            operator: conditionData.operator || 'and',
            condition_ids: (conditionData.conditions || []).map((c: any) => ({
              attribute: c.attribute,
              condition: formatConditionValue(c.condition),
              value: c.value,
              // Map the field_type (e.g. "datetime") to a value_type the backend
              // Selection accepts; sending the raw type was rejected on update.
              value_type: mapFieldTypeToValueType(c.valueType, c.value),
            })),
            subgroup_ids: [],
          };
          break;
        }

        case 'emailBuilder':
        case 'notificationBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          itemType = 'action';
          const actionData = nodeConfig.data as any;
          relatedObject = {
            action_type: formatActionTypeKey(actionData.actionType || node.type),
            title: actionData.title || '',
            return_value: actionData.returnValue || actionData.content || actionData.message || '',
          };
          break;
        }

        case 'productBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;

          const detailValues: any = {};
          if (actionData.unit) {
            detailValues.unit = actionData.unit;
          }
          if (actionData.amount) {
            detailValues.amount = actionData.amount;
          }

          itemType = 'action';
          relatedObject = {
            action_type: 'product',
            return_value: actionData.selectedProductId ? actionData.selectedProductId.toString() : '',
            ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
          };
          break;
        }

        case 'caseBuilder': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          const actionData = nodeConfig.data as any;

          const detailValues: any = {};
          if (actionData.sectionId) {
            detailValues.section = actionData.sectionId;
          }

          itemType = 'action';
          relatedObject = {
            action_type: 'case',
            title: actionData.title || '',
            return_value: actionData.message || '',
            ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
          };
          break;
        }

        case 'action': {
          if (!nodeConfig || nodeConfig.nodeType !== 'action') {
            throw new Error(`No configuration found for action node ${nodeId}`);
          }
          itemType = 'action';
          const actionData = nodeConfig.data as any;
          const actionTypeKey = formatActionTypeKey(actionData.actionType || 'action');

          if (actionTypeKey === 'product') {
            const detailValues: any = {};
            if (actionData.unit) {
              detailValues.unit = actionData.unit;
            }
            if (actionData.amount) {
              detailValues.amount = actionData.amount;
            }
            relatedObject = {
              action_type: 'product',
              return_value: actionData.selectedProductId
                ? actionData.selectedProductId.toString()
                : actionData.returnValue || '',
              ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
            };
          } else {
            const detailValues: any = {};
            if (actionTypeKey === 'notification' && actionData.partnerId) {
              detailValues.recipient_user_id = actionData.partnerId;
              if (actionData.partnerName) detailValues.recipient_user_name = actionData.partnerName;
            }
            if (actionTypeKey === 'mail' && actionData.recipient && actionData.recipient !== RECORD_CONTACT_RECIPIENT) {
              detailValues.recipient_email = actionData.recipient;
            }
            relatedObject = {
              action_type: actionTypeKey,
              title: actionData.title || '',
              return_value: actionData.returnValue || actionData.content || actionData.message || '',
              ...(Object.keys(detailValues).length > 0 && { detail_values: detailValues }),
            };
          }
          break;
        }

        default:
          throw new Error(`Unknown node type: ${node.type}`);
      }

      return {
        id: null,
        item_type: itemType,
        related_object: relatedObject,
      };
    };

    const buildWorkflowTree = (nodeId: string): any => {
      const currentItem = buildWorkflowItem(nodeId);
      if (!currentItem) return null;

      const nextNodeId = findNextWorkflowNode(nodeId);

      if (nextNodeId) {
        const nextItem = buildWorkflowTree(nextNodeId);
        if (nextItem) {
          currentItem.true_result = nextItem;
        }
      }

      return currentItem;
    };

    const root = buildWorkflowTree(triggerNode.id);
    if (!root) {
      throw new Error('Failed to build workflow tree');
    }

    return {
      name: title,
      description: description || null,
      one_time: oneTimeValue,
      root,
    };
  };

  const issues = useMemo(
    () =>
      isEditMode
        ? validateWorkflow(workflowNodes, workflowsState.nodeConfigurations, !!workflowsState.selectedTrigger, t)
        : [],
    [isEditMode, workflowNodes, workflowsState.nodeConfigurations, workflowsState.selectedTrigger, t],
  );

  const validationValue = useMemo(() => ({ issues, showIssues }), [issues, showIssues]);

  const ensureValid = () => {
    if (!issues.length) return true;
    setShowIssues(true);
    toast.error(t('workflows.validation.fix_before_save'));
    return false;
  };

  const handleUpdateWorkflow = async (title: string, description: string, oneTimeValue: boolean = oneTime) => {
    if (!ensureValid()) return;
    try {
      setIsUpdating(true);

      const workflowData = transformWorkflowDataForUpdate(
        title,
        description,
        workflowNodes,
        workflowEdges,
        oneTimeValue,
      );

      await updateWorkflowMutation.mutateAsync({
        params: {
          query: { workflow_id: Number(id) },
        },
        body: workflowData as any,
      });

      toast.success(t('success.workflow_updated'));
      setIsModalOpen(false);
      setIsEditMode(false);
      setSearchParams({});
      dispatch(clearAllNodeConfigurations());
      navigate('/workflows');
    } catch (error) {
      console.error('Error updating workflow:', error);
      toast.error(t('errors.workflow_update_failed'));
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || initialWorkflowState || !workflow || !workflowsState.selectedTrigger) return undefined;
    const timer = setTimeout(() => {
      const settled = getWorkflows(store.getState());
      setInitialWorkflowState({
        selectedTrigger: settled.selectedTrigger,
        nodeConfigurations: { ...settled.nodeConfigurations },
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [isEditMode, initialWorkflowState, workflow, workflowsState.selectedTrigger]);

  const handleEditClick = () => {
    setIsEditMode(true);
    setSearchParams({ edit: 'true' });

    setInitialWorkflowState({
      selectedTrigger: workflowsState.selectedTrigger,
      nodeConfigurations: { ...workflowsState.nodeConfigurations },
    });
  };

  const hasUnsavedChanges = useMemo(() => {
    if (!isEditMode) return false;

    if (!initialWorkflowState) return false;

    const triggerChanged =
      JSON.stringify(workflowsState.selectedTrigger) !== JSON.stringify(initialWorkflowState.selectedTrigger);
    const configurationsChanged =
      JSON.stringify(workflowsState.nodeConfigurations) !== JSON.stringify(initialWorkflowState.nodeConfigurations);

    return triggerChanged || configurationsChanged;
  }, [isEditMode, workflowsState.selectedTrigger, workflowsState.nodeConfigurations, initialWorkflowState]);

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setIsCancelConfirmationOpen(true);
    } else {
      performCancel();
    }
  };

  const performCancel = () => {
    if (isEditMode) {
      setIsEditMode(false);
      setSearchParams({});
      dispatch(clearAllNodeConfigurations());
      if (workflow) {
        const { nodes: originalNodes, edges: originalEdges } = transformWorkflowToNodesAndEdges(workflow);
        setWorkflowNodes(originalNodes);
        setWorkflowEdges(originalEdges);
      }
      setFlowResetKey(key => key + 1);
    } else {
      navigate('/workflows');
    }
  };

  const handleConfirmCancel = (confirmed: boolean) => {
    setIsCancelConfirmationOpen(false);
    if (confirmed) {
      performCancel();
    }
  };

  const buttons = [
    ...(isEditMode
      ? [
          {
            title: t('general.cancel'),
            tone: ButtonToneProps.SECONDARY,
            onClick: handleCancelClick,
          },
        ]
      : []),
    ...(!isEditMode && workflow
      ? [
          {
            title: t('workflows.duplicate'),
            tone: ButtonToneProps.SECONDARY,
            onClick: () =>
              navigate('/workflows/create', {
                state: {
                  root: workflow.root,
                  name: `${workflow.name} (${t('workflows.copy')})`,
                  description: workflow.description ?? '',
                  oneTime: workflow.one_time,
                  triggerName: workflow.trigger_name ?? undefined,
                },
              }),
          },
        ]
      : []),
    {
      title: isEditMode ? t('general.save') : t('workflows.edit_workflow'),
      tone: ButtonToneProps.PRIMARY_BLUE,
      onClick: !isEditMode ? handleEditClick : () => handleUpdateWorkflow(title, description, oneTime),
    },
  ];

  const displayNodes = workflowNodes;
  const displayEdges = workflowEdges;

  if (isWorkflowLoading) {
    return (
      <Container title={t('workflows.view_workflow')} buttons={[]}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-faded">{t('general.loading')}</p>
          </div>
        </div>
      </Container>
    );
  }

  if (!workflow) {
    return (
      <Container title={t('workflows.view_workflow')} buttons={[]}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-faded">{t('errors.workflow_not_found')}</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container
        key={workflow?.id || 'loading'}
        title={`Workflows / ${workflow?.name || t('workflows.view_workflow')}`}
        buttons={buttons}
        isEditMode={isEditMode}
        editAction={isEditMode ? () => setIsModalOpen(true) : undefined}
        hint={isEditMode && hasUnsavedChanges ? t('workflows.unsaved_changes_hint') : undefined}>
        {!isEditMode && (
          <div className="mb-16">
            <SegmentedControl
              ariaLabel={t('navbar.workflows')}
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { value: 'structure', label: t('workflows.tab_structure') },
                { value: 'runs', label: `${t('workflows.tab_runs')} (${workflow.runs?.lifetime?.total ?? 0})` },
              ]}
            />
          </div>
        )}
        {!isEditMode && activeTab === 'runs' ? (
          <WorkflowRuns workflowId={workflow.id} stats={workflow.runs ?? EMPTY_RUN_STATS} />
        ) : (
          <WorkflowValidationContext.Provider value={validationValue}>
            {isEditMode && showIssues && issues.length > 0 && (
              <div className="mb-16 rounded-base status-error-surface status-error-full px-16 py-12 flex flex-col gap-y-4">
                <span className="typo-text-14--bold">{t('workflows.validation.title')}</span>
                {issues.map(issue => (
                  <span key={`${issue.nodeId}-${issue.message}`} className="typo-text-12">
                    {issue.message}
                  </span>
                ))}
              </div>
            )}
            <div style={{ width: '100%', height: 'calc(100dvh - 6.4rem)' }}>
              <ReactFlow
                key={flowResetKey}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                panOnDrag
                nodes={displayNodes}
                onNodesChange={isEditMode ? onNodesChange : undefined}
                edges={displayEdges}
                onEdgesChange={isEditMode ? onEdgesChange : undefined}
                proOptions={{ hideAttribution: true }}>
                <Controls />
                <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
              </ReactFlow>

              {isEditMode && workflowsState.showNodesType && (
                <div
                  style={{
                    position: 'absolute',
                    left: calculateLeftPosition,
                    top: calculateTopPosition,
                  }}>
                  <NodePanel data={panelOptions} />
                </div>
              )}
            </div>
          </WorkflowValidationContext.Provider>
        )}
      </Container>

      <SaveWorkflowModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleUpdateWorkflow}
        isLoading={isUpdating}
        initialTitle={title}
        initialDescription={description}
        initialOneTime={oneTime}
        setTitle={setTitle}
        setDescription={setDescription}
        setOneTime={setOneTime}
        isEditMode
      />

      <ConfirmationModal
        isOpen={isCancelConfirmationOpen}
        title={t('workflows.cancel_changes')}
        description={t('workflows.cancel_changes_confirmation')}
        onClose={handleConfirmCancel}
      />
    </>
  );
}

function WorkflowsWithProvider() {
  return (
    <ReactFlowProvider>
      <WorkflowView />
    </ReactFlowProvider>
  );
}

export default WorkflowsWithProvider;
